import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Monitor } from "lucide-react";
import { useRegister } from "../../api/auth";
import { ApiError } from "../../api/client";

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();

  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    try {
      await register.mutateAsync({
        companyName,
        contactEmail: email,
        firstName,
        lastName,
        password,
      });
      // Registration created the Cognito user + DDB rows. Bounce the
      // user to sign-in so they hit the normal SRP flow.
      navigate("/sign-in?registered=1");
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { message?: string } | undefined;
        setError(body?.message ?? `Registration failed (${err.status})`);
      } else {
        setError(err instanceof Error ? err.message : "Registration failed");
      }
    }
  }

  const loading = register.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-riot-navy-900">
      <div className="w-full max-w-md">
        <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800 px-8 py-10 shadow-sm">
          <div className="mb-6 flex flex-col items-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-riot-navy-800">
              <Monitor size={24} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Create Account</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">Register your company on RIoT-FOTA</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Company Name</label>
              <input aria-label="Company Name" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">First Name</label>
                <input aria-label="First Name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Last Name</label>
                <input aria-label="Last Name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Email</label>
              <input aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Password</label>
              <input aria-label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Confirm Password</label>
              <input aria-label="Confirm Password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500" />
            </div>

            {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800/60 dark:bg-red-950/30 dark:text-red-300">{error}</div>}

            <button type="submit" disabled={loading} className="w-full rounded-md bg-riot-gold-400 px-4 py-2.5 text-sm font-medium text-riot-navy-900 shadow-sm hover:bg-riot-gold-500 active:bg-riot-gold-600 focus:outline-none focus:ring-2 focus:ring-riot-gold-400 focus:ring-offset-2 disabled:opacity-50">
              {loading ? "Creating account..." : "Register"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-gray-500 dark:text-riot-navy-300">
            Already have an account? <Link to="/sign-in" className="font-medium text-riot-gold-600 hover:underline dark:text-riot-gold-400">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
